import { HttpErrorResponse } from '@angular/common/http';

/** Raised when the chat API rejects the session (expired / unauthorized). */
export class AuthSessionError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AuthSessionError';
    this.status = status;
  }
}

export const UNAUTHORIZED_STATUS = 401;
export const FORBIDDEN_STATUS = 403;

export function isUnauthorizedStatus(status: number): boolean {
  return status === UNAUTHORIZED_STATUS || status === FORBIDDEN_STATUS;
}

const AUTH_CONNECTION_ERROR_CODE = 'auth_connection_error';
/** Gateway failures where the refresh request almost certainly never reached auth.
 * Status 0 / 500 are excluded: the request may have rotated the refresh token
 * before the client saw a response, and a retry would then force a logout. */
const TRANSIENT_REFRESH_STATUSES = new Set([502, 503, 504]);

/** Refresh failed because auth service was unreachable, not because the session died. */
export function isTransientAuthRefreshError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || isUnauthorizedStatus(error.status)) {
    return false;
  }

  if (readAuthErrorCode(error) === AUTH_CONNECTION_ERROR_CODE) {
    return true;
  }

  return TRANSIENT_REFRESH_STATUSES.has(error.status);
}

export function isAuthSessionError(error: unknown): error is AuthSessionError | HttpErrorResponse {
  if (error instanceof AuthSessionError) {
    return true;
  }

  return error instanceof HttpErrorResponse && isUnauthorizedStatus(error.status);
}

function readAuthErrorCode(error: HttpErrorResponse): string | undefined {
  const body = error.error;

  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const nestedCode = (body as { error?: { code?: unknown } }).error?.code;

  if (typeof nestedCode === 'string') {
    return nestedCode;
  }

  const code = (body as { code?: unknown }).code;

  return typeof code === 'string' ? code : undefined;
}
